import appConfig from "../../config/app.config";
import Epayment from "./epayment.service";
import Op from "./op.service";
import { withBrowserAuth } from "./browser-session";

const FALLBACK_INTERVAL = 60000;
const STREAM_TIMEOUT = 45000;

const normalizeStatus = (value) => {
  const status = String(value || "")
    .trim()
    .toUpperCase();
  return (
    {
      "FOR PAYMENT": "I",
      PENDING: "I",
      PAID: "S",
      SUCCESS: "S",
      SUCCESSFUL: "S",
      CANCELLED: "C",
      CANCELED: "C",
      FAILED: "F",
      EXPIRED: "E",
    }[status] || status
  );
};

// Parse complete SSE frames; fetch chunks can split lines and UTF-8 characters.
export const createEventParser = (onEvent) => {
  let buffer = "";
  return (chunk) => {
    buffer += chunk;
    let boundary = /\r?\n\r?\n/.exec(buffer);
    while (boundary) {
      const frame = buffer.slice(0, boundary.index);
      buffer = buffer.slice(boundary.index + boundary[0].length);
      let event = "message";
      const data = [];
      frame.split(/\r?\n/).forEach((line) => {
        if (line.startsWith("event:")) event = line.slice(6).trim();
        if (line.startsWith("data:"))
          data.push(line.slice(5).replace(/^ /, ""));
      });
      if (data.length) onEvent(event, JSON.parse(data.join("\n")));
      boundary = /\r?\n\r?\n/.exec(buffer);
    }
    if (buffer.length > 65536) throw new Error("Payment event is too large");
  };
};

export default function subscribeToPayment(billingNo, onUpdate, onConnection) {
  let stopped = false;
  let healthy = false;
  let controller;
  let fallbackController;
  let reconnectTimer;
  let fallbackTimer;
  let watchdog;
  let retryDelay = 3000;
  let lastFallback = 0;
  let fallbackRunning = false;
  let revision = 0;
  let lastEorDetails = null;

  const isAccessDenied = (error) =>
    error?.code === "PAYMENT_SESSION_REQUIRED" ||
    [401, 403].includes(error?.response?.status);

  const stop = () => {
    stopped = true;
    healthy = false;
    controller?.abort();
    fallbackController?.abort();
    clearTimeout(reconnectTimer);
    clearTimeout(fallbackTimer);
    clearTimeout(watchdog);
  };

  const denyAccess = () => {
    if (stopped) return;
    stop();
    onConnection("denied");
  };

  const deliver = (data) => {
    if (stopped) return;
    const reference = data?.merchant_reference_number || data?.billing_no;
    if (reference !== billingNo) return;
    revision += 1;
    if (data.eor_details !== undefined) lastEorDetails = data.eor_details;
    onUpdate({
      ...data,
      eor_details: lastEorDetails,
      status: normalizeStatus(data.status),
    });
  };

  const fallback = async () => {
    if (stopped || healthy || fallbackRunning) return;
    const delay = FALLBACK_INTERVAL - (Date.now() - lastFallback);
    if (delay > 0) {
      clearTimeout(fallbackTimer);
      fallbackTimer = setTimeout(fallback, delay);
      return;
    }
    lastFallback = Date.now();
    fallbackRunning = true;
    fallbackController = new AbortController();
    const options = { signal: fallbackController.signal, timeout: 15000 };
    const startRevision = revision;
    try {
      const response = await Epayment.getTransactionData_merchRefNo(
        {
          merchant_reference_number: billingNo,
        },
        options,
      );
      let { data } = response.data;
      if (data?.Error_code === "ER1X006") {
        const bill = await Op.fetchOpList(billingNo, options);
        data = {
          ...bill.data.data,
          merchant_reference_number: billingNo,
          epayment_amount: bill.data.data.amount,
        };
      }
      // The gateway response has payment status but not the stored EOR details.
      // Fetch those only during the existing outage fallback, never on a healthy stream.
      if (!stopped && !healthy && revision === startRevision) {
        try {
          const snapshot = await Op.fetchPaymentSnapshot(billingNo, options);
          data = {
            ...snapshot.data.data,
            ...data,
            eor_details: snapshot.data.data.eor_details,
          };
        } catch (error) {
          if (isAccessDenied(error)) throw error;
          // A snapshot failure must not hide an otherwise valid payment update.
        }
      }
      if (!stopped && !healthy && revision === startRevision && data?.status) {
        deliver({
          ...data,
          merchant_reference_number:
            data.merchant_reference_number || billingNo,
        });
      }
    } catch (error) {
      if (isAccessDenied(error)) denyAccess();
      // Keep the last known data visible while the connection recovers.
    } finally {
      fallbackRunning = false;
      if (!stopped && !healthy) {
        clearTimeout(fallbackTimer);
        fallbackTimer = setTimeout(fallback, FALLBACK_INTERVAL);
      }
    }
  };

  const connect = async () => {
    if (stopped) return;
    controller = new AbortController();
    let reader;
    let permanentFailure = false;
    const armWatchdog = () => {
      clearTimeout(watchdog);
      watchdog = setTimeout(() => controller.abort(), STREAM_TIMEOUT);
    };
    armWatchdog();
    try {
      // Streaming fetch supports Authorization headers. Native EventSource
      // would require cookies or a credential in the URL for this application.
      const base = appConfig.api.opListBaseUrl.replace(/\/$/, "");
      const response = await fetch(
        `${base}/payment-events/${encodeURIComponent(billingNo)}`,
        withBrowserAuth({
          headers: { Accept: "text/event-stream" },
          cache: "no-store",
          signal: controller.signal,
        }),
      );
      if ([401, 403].includes(response.status)) {
        permanentFailure = true;
        throw new Error("Payment access denied");
      }
      if (
        !response.ok ||
        !response.headers.get("content-type")?.includes("text/event-stream") ||
        !response.body?.getReader
      )
        throw new Error("Payment stream unavailable");

      reader = response.body.getReader();
      const decoder = new TextDecoder();
      const parse = createEventParser((event, data) => {
        if (stopped || event !== "payment.updated") return;
        const reference = data?.merchant_reference_number || data?.billing_no;
        if (reference !== billingNo || !data.status) return;
        healthy = true;
        armWatchdog();
        fallbackController?.abort();
        retryDelay = 3000;
        clearTimeout(fallbackTimer);
        onConnection("connected");
        deliver(data);
      });
      // Heartbeats reset the watchdog, but only a payment snapshot establishes
      // a usable connection. Each reconnect receives a fresh saved snapshot.
      while (!stopped) {
        const { value, done } = await reader.read(); // eslint-disable-line no-await-in-loop
        if (done) break;
        if (healthy) armWatchdog();
        parse(decoder.decode(value, { stream: true }));
      }
    } catch (error) {
      if (isAccessDenied(error)) permanentFailure = true;
      // Network failures, unsupported streams and malformed frames reconnect.
    } finally {
      clearTimeout(watchdog);
      controller.abort();
      if (reader) {
        await reader.cancel().catch(() => {});
        reader.releaseLock();
      }
      healthy = false;
      if (!stopped) {
        if (!permanentFailure) {
          onConnection("reconnecting");
          fallback();
          if (!stopped) {
            reconnectTimer = setTimeout(connect, retryDelay);
            retryDelay = Math.min(retryDelay * 2, 30000);
          }
        } else {
          denyAccess();
        }
      }
    }
  };

  connect();
  return stop;
}
